/**
 * Tech availability (TECH-AVAIL-01) — sick days, vacations, personal days,
 * meetings, errands, training. Pegasus-native typed events (never ESC); the
 * board derives OUT states + blocking chips from these, and the conflict system
 * flags calls sitting on an out tech. See research/tech-availability-design.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type AvailKind =
  | "sick"
  | "vacation"
  | "personal"
  | "meeting"
  | "errand"
  | "part_pickup"
  | "training"
  | "other";

export const AVAIL_KIND_LABEL: Record<AvailKind, string> = {
  sick: "Sick",
  vacation: "Vacation",
  personal: "Personal day",
  meeting: "Meeting",
  errand: "Errand",
  part_pickup: "Part pickup",
  training: "Training",
  other: "Off",
};

export interface AvailEvent {
  id: string;
  empno: string;
  kind: AvailKind;
  start_at: string; // ISO
  end_at: string; // ISO
  all_day: boolean;
  title: string | null;
  location: string | null;
  location_lat: number | null; // set when picked via Places → map waypoint
  location_lng: number | null;
  status: "requested" | "approved" | "denied";
  group_id: string | null;
  /** RECUR-AVAIL-01 — set on every occurrence of a repeating series. */
  series_id: string | null;
  created_by: string | null;
  /** SICK-02 — self-reported sick: the ESST purpose (sick | family | safety | other), and
   *  whether it was filed after the company's notice cutoff (DB-stamped). */
  category?: string | null;
  late_notice?: boolean;
}

/** Board/office label for a self-reported out day: "Sick", "Family member", "Safety leave". */
export function availOutLabel(e: { kind: AvailKind; category?: string | null }): string {
  if (e.kind === "sick") {
    return e.category === "family" ? "Family member" : e.category === "safety" ? "Safety leave" : e.category === "other" ? "Out" : "Sick";
  }
  return AVAIL_KIND_LABEL[e.kind];
}

/** Local-midnight bounds for a YYYY-MM-DD board date. */
export function dayBounds(dateISO: string): { start: string; end: string } {
  const start = new Date(`${dateISO}T00:00:00`);
  const end = new Date(`${dateISO}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return { start: start.toISOString(), end: end.toISOString() };
}

/** All availability events overlapping the given board date (not denied). */
export function useTechAvailability(dateISO: string) {
  const { start, end } = dayBounds(dateISO);
  return useQuery({
    queryKey: ["tech_availability", dateISO],
    queryFn: async (): Promise<AvailEvent[]> => {
      const { data, error } = await supabase
        .from("tech_availability")
        .select("*")
        .lt("start_at", end)
        .gt("end_at", start)
        .neq("status", "denied")
        .order("start_at");
      if (error) throw error;
      return (data ?? []) as AvailEvent[];
    },
    staleTime: 30 * 1000,
  });
}

/** A tech's upcoming events (for the modal's manage/cancel list). */
export function useUpcomingAvailability(empno: string | null) {
  return useQuery({
    queryKey: ["tech_availability_upcoming", empno],
    enabled: Boolean(empno),
    queryFn: async (): Promise<AvailEvent[]> => {
      const { data, error } = await supabase
        .from("tech_availability")
        .select("*")
        .eq("empno", empno as string)
        .gt("end_at", new Date().toISOString())
        .neq("status", "denied")
        .order("start_at")
        .limit(80);
      if (error) throw error;
      return (data ?? []) as AvailEvent[];
    },
    staleTime: 15 * 1000,
  });
}

export interface NewAvailability {
  empnos: string[]; // 1 tech, or several for a meeting (fanned out, shared group)
  kind: AvailKind;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD (same as start for one day)
  allDay: boolean;
  startTime?: string; // HH:MM when !allDay
  endTime?: string; // HH:MM when !allDay
  title?: string;
  location?: string;
  locationLat?: number | null;
  locationLng?: number | null;
  /** RECUR-AVAIL-01 — repeat weekly/biweekly on the start date's weekday,
   *  materialized through repeatUntil (set it once: "off by 2pm every Monday"). */
  repeat?: "none" | "weekly" | "biweekly";
  repeatUntil?: string; // YYYY-MM-DD
}

/** The stored span for a form's dates/times (all-day = exclusive next-midnight end). */
export function computeSpan(v: {
  startDate: string;
  endDate: string;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
}): { start_at: string; end_at: string } {
  const startAt = v.allDay
    ? new Date(`${v.startDate}T00:00:00`)
    : new Date(`${v.startDate}T${v.startTime ?? "09:00"}:00`);
  const endAt = v.allDay
    ? (() => {
        const d = new Date(`${v.endDate}T00:00:00`);
        d.setDate(d.getDate() + 1);
        return d;
      })()
    : new Date(`${v.endDate}T${v.endTime ?? "10:00"}:00`);
  return { start_at: startAt.toISOString(), end_at: endAt.toISOString() };
}

export function useAddAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: NewAvailability) => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      // RECUR-AVAIL-01: a repeating block materializes one row per occurrence
      // (weekly or biweekly on the start date's weekday) through repeatUntil,
      // capped at 52 — simple rows, so the board/conflict queries don't change
      // and any single occurrence can still be deleted on its own.
      const repeating = (v.repeat ?? "none") !== "none" && !!v.repeatUntil;
      const stepDays = v.repeat === "biweekly" ? 14 : 7;
      const shiftISO = (iso: string, days: number) => {
        const d = new Date(iso + "T00:00:00");
        d.setDate(d.getDate() + days);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      };
      const occurrences: { startDate: string; endDate: string }[] = [];
      if (repeating) {
        let cur = v.startDate;
        while (cur <= (v.repeatUntil as string) && occurrences.length < 52) {
          occurrences.push({ startDate: cur, endDate: cur });
          cur = shiftISO(cur, stepDays);
        }
      } else {
        occurrences.push({ startDate: v.startDate, endDate: v.endDate });
      }
      const seriesId = repeating ? crypto.randomUUID() : null;
      const rows = occurrences.flatMap((occ) => {
        const span = computeSpan({ ...v, startDate: occ.startDate, endDate: occ.endDate });
        const group = v.empnos.length > 1 ? crypto.randomUUID() : null;
        return v.empnos.map((empno) => ({
          empno,
          kind: v.kind,
          start_at: span.start_at,
          end_at: span.end_at,
          all_day: v.allDay,
          title: v.title?.trim() || null,
          location: v.location?.trim() || null,
          location_lat: v.locationLat ?? null,
          location_lng: v.locationLng ?? null,
          status: "approved", // approval flow lands with PERM-04 + the Tech role
          group_id: group,
          series_id: seriesId,
          created_by: email,
        }));
      });
      const { error } = await supabase.from("tech_availability").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tech_availability"] });
      qc.invalidateQueries({ queryKey: ["tech_availability_upcoming"] });
    },
  });
}

/**
 * Edit an existing event. For a multi-tech meeting (shared group_id) the change
 * applies to every attendee's row, so the meeting stays one meeting.
 */
export function useUpdateAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      group_id: string | null;
      kind: AvailKind;
      startDate: string;
      endDate: string;
      allDay: boolean;
      startTime?: string;
      endTime?: string;
      title?: string;
      location?: string;
      locationLat?: number | null;
      locationLng?: number | null;
    }) => {
      const span = computeSpan(v);
      const patch = {
        kind: v.kind,
        start_at: span.start_at,
        end_at: span.end_at,
        all_day: v.allDay,
        title: v.title?.trim() || null,
        location: v.location?.trim() || null,
        location_lat: v.locationLat ?? null,
        location_lng: v.locationLng ?? null,
        updated_at: new Date().toISOString(),
      };
      const q = supabase.from("tech_availability").update(patch);
      const { error } = v.group_id
        ? await q.eq("group_id", v.group_id)
        : await q.eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tech_availability"] });
      qc.invalidateQueries({ queryKey: ["tech_availability_upcoming"] });
    },
  });
}

/** Cancel a repeating series — FUTURE occurrences only; the past stays. */
export function useCancelSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (seriesId: string) => {
      const { error } = await supabase
        .from("tech_availability")
        .delete()
        .eq("series_id", seriesId)
        .gt("end_at", new Date().toISOString());
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tech_availability"] });
      qc.invalidateQueries({ queryKey: ["tech_availability_upcoming"] });
    },
  });
}

export function useCancelAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("tech_availability")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tech_availability"] });
      qc.invalidateQueries({ queryKey: ["tech_availability_upcoming"] });
    },
  });
}

/** Group a day's events by tech, with the OUT flag (any all-day event). */
export function availabilityByTech(events: AvailEvent[]): Map<
  string,
  { out: AvailEvent | null; timed: AvailEvent[] }
> {
  const map = new Map<string, { out: AvailEvent | null; timed: AvailEvent[] }>();
  for (const e of events) {
    const cur = map.get(e.empno) ?? { out: null, timed: [] };
    if (e.all_day) {
      // first all-day event wins the banner (rare to have two)
      cur.out = cur.out ?? e;
    } else {
      cur.timed.push(e);
    }
    map.set(e.empno, cur);
  }
  return map;
}
